import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiDownload, FiStar } from 'react-icons/fi';
import AgentBadge from './AgentBadge';

interface SkillData {
  _id: string;
  name: string;
  slug?: string;
  owner?: string;
  repoOwner?: string;
  description: string;
  categories?: string[];
  tags?: string[];
  compatibleAgents?: string[];
  installCount?: number;
  stars?: number;
  starCount?: number;
  authorAvatar?: string;
  authorUsername?: string;
  authorId?: { username?: string; displayName?: string; avatarUrl?: string };
}

interface SkillCardProps {
  skill: SkillData;
  index?: number;
}

function formatCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}k`;
  return n.toString();
}

export default function SkillCard({ skill, index = 0 }: SkillCardProps) {
  const navigate = useNavigate();

  // Derive owner from multiple possible sources
  const owner = skill.owner || skill.repoOwner || skill.slug?.split('/')[0] || 'unknown';
  const authorName = skill.authorUsername || skill.authorId?.username || owner;

  const handleClick = () => {
    navigate(`/s/${owner}/${skill.name}`);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.08, ease: [0.4, 0, 0.2, 1] }}
      whileHover={{ y: -6, transition: { duration: 0.25 } }}
      onClick={handleClick}
      className="card-3d cursor-pointer"
    >
      <div className="card-3d-inner glass-card p-6 h-full flex flex-col gap-4">
        {/* Header: Name + Stats */}
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-white truncate">
              {skill.name}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              {owner}/{skill.name}
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 text-xs text-slate-500">
            {(skill.stars !== undefined || skill.starCount !== undefined) && (
              <span className="flex items-center gap-1">
                <FiStar size={12} className="text-amber-400" />
                {formatCount(skill.stars ?? skill.starCount ?? 0)}
              </span>
            )}
            <span className="flex items-center gap-1">
              <FiDownload size={12} className="text-brand-400" />
              {formatCount(skill.installCount || 0)}
            </span>
          </div>
        </div>

        {/* Description */}
        <p className="text-sm text-slate-400 leading-relaxed line-clamp-2 flex-1">
          {skill.description || 'No description provided.'}
        </p>

        {/* Agent Badges */}
        {skill.compatibleAgents && skill.compatibleAgents.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {skill.compatibleAgents.slice(0, 5).map((agent) => (
              <AgentBadge key={agent} agentName={agent} size="sm" showLabel={false} />
            ))}
            {skill.compatibleAgents.length > 5 && (
              <span className="text-[10px] text-slate-500 px-2 py-0.5 rounded-full bg-white/5">
                +{skill.compatibleAgents.length - 5}
              </span>
            )}
          </div>
        )}

        {/* Category Tags */}
        {skill.categories && skill.categories.length > 0 && (
          <div className="flex flex-wrap gap-1.5">
            {skill.categories.slice(0, 3).map((cat) => (
              <span
                key={cat}
                className="text-[10px] px-2 py-0.5 rounded-md bg-brand-500/10 text-brand-300 border border-brand-500/10"
              >
                {cat}
              </span>
            ))}
          </div>
        )}

        {/* Author */}
        <div className="flex items-center gap-2 pt-2 border-t border-white/5">
          <div className="w-5 h-5 rounded-full bg-gradient-to-br from-brand-500/50 to-brand-purple-600/50 flex items-center justify-center text-[9px] text-white font-bold">
            {authorName[0].toUpperCase()}
          </div>
          <span className="text-xs text-slate-500">
            {authorName}
          </span>
        </div>
      </div>
    </motion.div>
  );
}

export type { SkillData };
